import React, { useState } from 'react';

import { ProcedureTypes, VertebralLevels } from '../../../models/DataAdminTypes';

export interface ProcedureRowData {
  id: string;
  startLevel: string;
  endLevel: string;
  procedureType: string;
  isValid: boolean|null;
}

interface ProcedureRowProps {
  data: ProcedureRowData;
  onUpdate: (id: string, field: keyof Omit<ProcedureRowData, 'id'>, value: string) => void;
  onRemove: (id: string) => void;
  showAddButton: boolean;
  showLabels: boolean;
  showRemoveButton: boolean;
}

export const ProcedureRow: React.FC<ProcedureRowProps> = ({ 
  data, 
  onUpdate, 
  onRemove,
  showAddButton,
  showLabels,
  showRemoveButton 
}) => {
  const [isEndlevelEnabled, setIsEndlevelEnabled] = useState<boolean>(false);
  const [filteredEndLevelValues, setFilteredEndLevelValues] = useState<string[]>(Object.values(VertebralLevels));

  const getVertebralLevelsFromLevel = (startLevel: string): string[] => {
    // Find the key for the start level
    const startKey = Object.entries(VertebralLevels)
      .find(([_, value]) => value === startLevel)?.[0];
    
    if (!startKey) {
      return [];
    }
    
    // Filter levels with keys >= startKey and return the values
    return Object.entries(VertebralLevels)
      .filter(([key, _]) => parseInt(key) >= parseInt(startKey))
      .map(([_, value]) => value);
  };
  
  const handleStartLevelUpdate = (id: string, value: string) => {
    // Filter VertebralLevels to only include levels that have a name greater than the selected star level name:
    const filteredValues = getVertebralLevelsFromLevel(value);
      
    setFilteredEndLevelValues(filteredValues);
    setIsEndlevelEnabled(true);
    onUpdate(id, 'startLevel', value);
  };

  return (
    <div data-testid="procedure-row" className="flex gap-6 items-end">
      <div data-testid="property-start-level" className="flex-5">
        {showLabels && (
          <div className="text-left">
            <label>Starting Surgical Level </label> <span className="text-red-500">*</span>
          </div>
        )}
        <div className="text-left">
          <select
            data-testid="input-start-level"
            name={`startLevel-${data.id}`} 
            value={data.startLevel}
            onChange={e => handleStartLevelUpdate(data.id, e.target.value)}
          >
            <option value="">Select Level</option>
            {Object.values(VertebralLevels).map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
      </div>

      <div data-testid="property-end-level" className="flex-5">
        {showLabels && (
          <div className="text-left">
            <label>Ending Surgical Level </label><span className="text-red-500">*</span>
          </div>
        )}
        <div className="text-left">
          <select 
            data-testid="input-end-level"
            disabled={!isEndlevelEnabled}
            name={`endLevel-${data.id}`} 
            className={`mt-1 ${!isEndlevelEnabled && 'form-field-disabled'} `}
            value={data.endLevel}
            onChange={e => onUpdate(data.id, 'endLevel', e.target.value)}
          >
            <option value="">Select Level</option>
            {Object.values(filteredEndLevelValues).map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
      </div>

      <div data-testid="procedure-type" className="flex-10">
        {showLabels && (
          <div className="text-left">
            <label>Procedure Type </label><span className="text-red-500">*</span>
          </div>
        )}
        <div className="text-left">
          <select
            data-testid="select-procedure-type"
            name={`procedureType-${data.id}`} 
            className="mt-1"
            value={data.procedureType}
            onChange={e => onUpdate(data.id, 'procedureType', e.target.value)}
          >
            <option value="">Select Type</option>
            {Object.keys(ProcedureTypes).map(procType => (
              <option key={procType} value={ProcedureTypes[procType as keyof typeof ProcedureTypes]}>
                {procType}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div data-testid="row-action" className="flex-1 flex items-center justify-center">
        <div className="w-8 flex justify-center">
          {showAddButton && (
            <button
              data-testid="add-procedure-button"
              className="add-row button-borderless"
              type="button"
              onClick={() => onUpdate('add', 'startLevel', '')}
              aria-label="Add procedure row"
            >
              +
            </button>
          )}
        </div>
        <div className="w-8 flex justify-center">
          {showRemoveButton && (
            <button
              className="add-row button-borderless"
              type="button"
              onClick={() => onRemove(data.id)}
              aria-label="Remove procedure row"
            >
              ×
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
